import React from 'react';
import { Card, CardContent, Typography, Box, useTheme } from "@mui/material";
import { tokens } from "../../assets/theme";
import CircularProgressWithLabel from '../../components/controls/CircularProgressWithLabel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import { FormattedMessage } from "react-intl";
import messages from "./messages";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatMinutesAsDuration } from "../../utils/durationUtils";

export default function AxionMachineCard(props) {
    const { id, item, fields, uncodedStopsAndStatus } = props;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const showStatusIcons = false;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
      };
    
    return (
        <Card 
            ref={setNodeRef} 
            style={style} 
            {...attributes} 
            {...listeners}
            sx={{ 
                backgroundColor: colors.primary[600], 
                width: 325, 
                borderRadius: "10px" 
            }}>
            <CardContent>
                <Box
                        width="80%"
                        m="10px auto"
                        p="5px"
                        height="60px"
                        display="flex"
                        justifyContent="center"
                        backgroundColor={
                            ((uncodedStopsAndStatus && uncodedStopsAndStatus.machineStatus === "Running") || (!uncodedStopsAndStatus && item.machineStatus === "Running"))
                            ? colors.dashbordGreen[600]
                            : ((uncodedStopsAndStatus && uncodedStopsAndStatus.machineStatus === "Offline") || (!uncodedStopsAndStatus && item.machineStatus === "Offline"))
                            ? colors.yellow[500]
                            : colors.redAccent[500]
                        }
                        borderRadius="10px"
                    >
                    <Typography sx={{ display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        textAlign: "center" }} 
                        variant={ item.machineName.length < 7 ? "h1" : "h2"} component="div">
                        { item.machineName } { !showStatusIcons ? null : ((uncodedStopsAndStatus && uncodedStopsAndStatus.machineStatus === "Running") || (!uncodedStopsAndStatus && item.machineStatus === "Running")) ? <CheckCircle fontSize='large' /> : <Error fontSize='large' /> }
                    </Typography>
                </Box>
                { ((uncodedStopsAndStatus && uncodedStopsAndStatus.machineStatus === "Stopped") || (!uncodedStopsAndStatus && item.machineStatus === "Stopped")) && (
                <Typography sx={{ textAlign: 'center', color: colors.primary[100] }} variant="h3" component="div">
                    { item.currentStop } <br />
                    { formatMinutesAsDuration(item.currentStopTime) }<br /><br />
                </Typography>
                )}
                { ((uncodedStopsAndStatus && uncodedStopsAndStatus.machineStatus !== "Stopped") || (!uncodedStopsAndStatus && item.machineStatus !== "Stopped")) && (
                <Typography sx={{ textAlign: 'center', color: colors.primary[100] }} variant="h3" component="div">
                    <br /><br /><br />
                </Typography>
                )}
                { fields.includes("Last CT") && (
                    <Box 
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={2} 
                        sx={{ width: "100%" }} >
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                Last CT:
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px", paddingLeft: "3px", paddingRight: "3px" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                            { Number.parseFloat(item.lastCycleTime).toFixed(2) } s
                            </Typography>
                        </Box>
                    </Box>
                )}
                { fields.includes("Last CT (color)") && (
                    <Box 
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={2} 
                        sx={{ width: "100%" }} >
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                Last CT:
                            </Typography>
                        </Box>
                        <Box 
                            sx={{ 
                                textAlign: "right",  
                                width: "30%",
                                marginBottom: "5px",
                                paddingLeft: "3px", paddingRight: "3px"
                            }}
                            backgroundColor={ () => {
                                if (item.lastCycleTime === 0 || item.lastCycleTime === "") {
                                  return colors.primary[300];
                                }
                            
                                var yellowExpandPercent = 0.05;
                                var redExpandPercent = 0.1;
                                var result = colors.dashbordGreen[600];
                                if (item.lastCycleTime > (item.idealCycleTimeActive + item.idealCycleTimeActive * redExpandPercent)) {
                                    result = colors.redAccent[500];
                                }
                                else if (item.lastCycleTime > (item.idealCycleTimeActive + item.idealCycleTimeActive * yellowExpandPercent)) {
                                    result = colors.yellow[600];
                                }
                                return result;
                              }}
                              borderRadius="4px"
                            >
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                            { Number.parseFloat(item.lastCycleTime).toFixed(2) } s
                            </Typography>
                        </Box>
                    </Box>
                )}
                { fields.includes("Avg CT") && (
                    <Box 
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={2} 
                        sx={{ width: "100%" }} >
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                Avg CT:
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px", paddingLeft: "3px", paddingRight: "3px" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                { Number.parseFloat(item.avgCycleTime).toFixed(2) } s
                            </Typography>
                        </Box>
                    </Box>
                )}
                { fields.includes("Avg CT (color)") && (
                    <Box 
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={2} 
                        sx={{ width: "100%" }} >
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px"  }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                Avg CT:
                            </Typography>
                        </Box>
                        <Box 
                            sx={{ 
                                textAlign: "right",  
                                width: "30%",
                                marginBottom: "5px",
                                paddingLeft: "3px", paddingRight: "3px"
                            }}
                            backgroundColor={ () => {
                                if (item.avgCycleTime === 0 || item.avgCycleTime === "") {
                                  return colors.primary[300];
                                }
                            
                                var yellowExpandPercent = 0.05;
                                var redExpandPercent = 0.1;
                                var result = colors.dashbordGreen[600];
                                if (item.avgCycleTime > (item.idealCycleTimeActive + item.idealCycleTimeActive * redExpandPercent)) {
                                    result = colors.redAccent[500];
                                }
                                else if (item.avgCycleTime > (item.idealCycleTimeActive + item.idealCycleTimeActive * yellowExpandPercent)) {
                                    result = colors.yellow[600];
                                }
                                return result;
                              }}
                              borderRadius="4px"
                            >
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                { Number.parseFloat(item.avgCycleTime).toFixed(2) } s
                            </Typography>
                        </Box>
                    </Box>
                )}
                { fields.includes("Ideal CT") && (
                    <Box 
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={2} 
                        sx={{ width: "100%" }} >
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px"}}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                Ideal CT:
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right",  width: "30%", marginBottom: "5px", paddingLeft: "3px", paddingRight: "3px" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                { Number.parseFloat(item.idealCycleTimeActive).toFixed(2) } s
                            </Typography>
                        </Box>
                    </Box>
                )}
                { fields.includes("Ideal Takt") && (
                    <Box 
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={2} 
                        sx={{ width: "100%" }} >
                        <Box sx={{ textAlign: "right",  width: "25%"  }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                id. Takt:
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right",  width: "25%" }}>
                            <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                                { Number.parseFloat(item.idealPace).toFixed(2) } s
                            </Typography>
                        </Box>
                    </Box>
                )}
                { fields.includes("Takt") && (
                    // <Box 
                    //     display="flex"
                    //     justifyContent="center"
                    //     alignItems="center"
                    //     gap={2} 
                    //     sx={{ width: "100%" }} >
                    //     <Box sx={{ textAlign: "right",  width: "25%"  }}>
                    //         <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                    //          Takt:
                    //         </Typography>
                    //     </Box>
                    //     <Box sx={{ textAlign: "right",  width: "25%" }}>
                    //         <Typography sx={{ color: colors.primary[100] }} variant="h4" component="div">
                    //             { Number.parseFloat(item.lastPace).toFixed(2) } s
                    //         </Typography>
                    //     </Box>
                    // </Box>

                    <Box width="80%"

                    p="5px"
              
                    m="10px auto"
          
                    display="flex"
                    justifyContent="center"


                    backgroundColor={ () => {
                        if (item.lastPace === 0 || item.lastPace === "") {
                          return colors.primary[300];
                        }
                    
                        var yellowExpandPercent = 0.05;
                        var redExpandPercent = 0.1;
                        var result = colors.dashbordGreen[600];
                        if (item.lastPace < (item.idealPace - item.idealPace * redExpandPercent)) {
                            result = colors.redAccent[500];
                        }
                        else if (item.lastPace < (item.idealPace - item.idealPace * yellowExpandPercent)) {
                            result = colors.yellow[600];
                        }
                        return result;
                      }}
                      borderRadius="4px"
                    >
                       <Typography sx={{ display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        textAlign: "center" }} 
                        variant="h1" component="div" color={colors.gray[100]}>
                            { props.machine?.speedUnitTypeId === 5 ? (
                                <>
                                    Takt / Ideal<br></br> {item.lastCycleTime > 0 ? Number.parseFloat(60 / item.lastCycleTime).toFixed(0) : "0"} / {item.idealCycleTimeActive > 0 ? Number.parseFloat(60 / item.idealCycleTimeActive).toFixed(0) : "-"}
                                </>
                            ) : (
                                <>
                                    Takt / Ideal<br></br> {Number.parseFloat(item.lastPace).toFixed(0)} / {item.idealPace}
                                </>
                            )}
                        </Typography>
                    </Box>
                )}
                { fields.includes("TAK") && (
                <Box width="80%"
                        m="20px auto"
                        p="5px"
                        display="flex"
                        justifyContent="center">
                    <CircularProgressWithLabel size={150} fontSize={24} value={ item.tak } circleColor={ colors.primary[300] } text={<FormattedMessage {...messages.oee} />} />
                </Box>
                )}
                { fields.includes("T") && (
                <Box width="80%"
                        m="20px auto"
                        p="5px"
                        display="flex"
                        justifyContent="center">
                    <CircularProgressWithLabel size={150} fontSize={24} value={ item.t } circleColor={ colors.primary[300] } text={<FormattedMessage {...messages.a} />} />
                </Box>
                )}
                { fields.includes("A") && (
                <Box width="80%"
                        m="20px auto"
                        p="5px"
                        display="flex"
                        justifyContent="center">
                    <CircularProgressWithLabel size={150} fontSize={24} value={ item.a } circleColor={ colors.primary[300] } text={<FormattedMessage {...messages.p} />} />
                </Box>
                )}
                { fields.includes("K") && (
                <Box width="80%"
                        m="20px auto"
                        p="5px"
                        display="flex"
                        justifyContent="center">
                    <CircularProgressWithLabel size={150} value={ item.k } circleColor={ colors.primary[300] } text={<FormattedMessage {...messages.q} />}/>
                </Box>
                )}
       
            </CardContent>
          </Card>
    )
}